'use client';

import React from 'react';
import { Check, Sparkles } from 'lucide-react';
import type { SectionProps } from '@/lib/storefront/section-templates';
import { safeHref } from '@/lib/storefront/safe-url';
import { containerClass, sectionStyle } from './HeaderSections';

// ──────────────────────────────────────────────────────────────
// 1. Three-tier pricing cards
// ──────────────────────────────────────────────────────────────

export function PricingCards({ content, style }: SectionProps) {
  const {
    eyebrow = 'Simple pricing',
    headline = 'Pick the plan that fits',
    subheadline = 'All plans include insurance, unlimited support and free cancellation up to 24 hours.',
    currency = 'DH',
    plans = [
      {
        name: 'Daily',
        price: '350',
        period: '/ day',
        description: 'Short trips and weekend escapes.',
        features: ['1–3 day rentals', '200 km per day included', 'Basic insurance', 'Airport pick-up'],
        ctaText: 'Choose daily',
        ctaHref: '/vehicles',
        highlighted: false,
      },
      {
        name: 'Weekly',
        price: '1,900',
        period: '/ week',
        description: 'The sweet spot for holidays.',
        features: ['4–7 day rentals', 'Unlimited kilometres', 'Full insurance', 'Free delivery in Agadir', 'Additional driver'],
        ctaText: 'Choose weekly',
        ctaHref: '/vehicles',
        highlighted: true,
        badge: 'Most popular',
      },
      {
        name: 'Monthly',
        price: '5,900',
        period: '/ month',
        description: 'Long stays and corporate use.',
        features: ['28+ day rentals', 'Unlimited kilometres', 'Zero-excess insurance', 'Vehicle swap once a month', 'Priority support'],
        ctaText: 'Choose monthly',
        ctaHref: '/vehicles',
        highlighted: false,
      },
    ],
  } = content;

  return (
    <section className="w-full bg-white" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{headline}</h2>
          {subheadline && <p className="mt-3 text-base text-slate-600">{subheadline}</p>}
        </div>

        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          {plans.map((plan: any, i: number) => (
            <div
              key={i}
              className={`relative flex flex-col rounded-2xl border p-6 ${
                plan.highlighted
                  ? 'border-brand bg-slate-900 text-white shadow-xl lg:-mt-3 lg:mb-3'
                  : 'border-slate-200 bg-white text-slate-900'
              }`}
            >
              {plan.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
                  {plan.badge}
                </span>
              )}
              <h3 className={`text-sm font-semibold uppercase tracking-wide ${plan.highlighted ? 'text-white/70' : 'text-slate-500'}`}>
                {plan.name}
              </h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-bold">{plan.price}</span>
                <span className={`text-sm ${plan.highlighted ? 'text-white/60' : 'text-slate-500'}`}>
                  {currency} {plan.period}
                </span>
              </div>
              <p className={`mt-2 text-sm ${plan.highlighted ? 'text-white/70' : 'text-slate-600'}`}>{plan.description}</p>

              <ul className="mt-6 flex-1 space-y-2.5">
                {plan.features.map((f: string, j: number) => (
                  <li key={j} className="flex items-start gap-2 text-sm">
                    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${plan.highlighted ? 'text-brand' : 'text-emerald-600'}`} />
                    <span className={plan.highlighted ? 'text-white/90' : 'text-slate-700'}>{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href={safeHref(plan.ctaHref)}
                className={`mt-6 inline-flex w-full items-center justify-center rounded-xl px-5 py-3 text-sm font-semibold ${
                  plan.highlighted ? 'bg-brand text-white hover:bg-brand/90' : 'border border-slate-300 text-slate-900 hover:bg-slate-50'
                }`}
              >
                {plan.ctaText}
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// 2. Comparison table
// ──────────────────────────────────────────────────────────────

export function PricingTable({ content, style }: SectionProps) {
  const {
    headline = 'Compare plans',
    subheadline = 'Everything included, side by side.',
    columns = ['Daily', 'Weekly', 'Monthly'],
    rows = [
      { label: 'Minimum rental', values: ['1 day', '4 days', '28 days'] },
      { label: 'Kilometres', values: ['200 / day', 'Unlimited', 'Unlimited'] },
      { label: 'Insurance', values: ['Basic', 'Full', 'Zero excess'] },
      { label: 'Delivery in Agadir', values: ['—', 'Free', 'Free'] },
      { label: 'Additional driver', values: ['30 DH/day', 'Included', 'Included'] },
      { label: 'Vehicle swap', values: ['—', '—', 'Monthly'] },
    ],
    ctaText = 'Book this plan',
    ctaHref = '/vehicles',
  } = content;

  return (
    <section className="w-full bg-slate-50" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{headline}</h2>
          {subheadline && <p className="mt-3 text-base text-slate-600">{subheadline}</p>}
        </div>

        <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Feature</th>
                {columns.map((c: string, i: number) => (
                  <th key={i} className="px-5 py-4 text-center text-sm font-semibold text-slate-900">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row: any, i: number) => (
                <tr key={i} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-3.5 font-medium text-slate-700">{row.label}</td>
                  {row.values.map((v: string, j: number) => (
                    <td key={j} className="px-5 py-3.5 text-center text-slate-600">
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <td className="px-5 py-4" />
                {columns.map((_: string, i: number) => (
                  <td key={i} className="px-5 py-4 text-center">
                    <a
                      href={safeHref(ctaHref)}
                      className={`inline-flex rounded-xl px-4 py-2 text-xs font-semibold ${
                        i === 1 ? 'bg-brand text-white' : 'border border-slate-300 text-slate-800'
                      }`}
                    >
                      {ctaText}
                    </a>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// 3. Single highlighted price card
// ──────────────────────────────────────────────────────────────

export function PricingSingle({ content, style }: SectionProps) {
  const {
    eyebrow = 'One price, everything in',
    headline = 'All-inclusive rental',
    price = '450',
    currency = 'DH',
    period = '/ day',
    description = 'Insurance, unlimited support and delivery across Agadir — included in every booking.',
    features = [
      'Comprehensive insurance',
      'Unlimited kilometres',
      'Free delivery and collection',
      'Additional driver at no cost',
      'Free cancellation up to 24h',
      '24/7 roadside assistance',
    ],
    ctaText = 'Book now',
    ctaHref = '/vehicles',
    note = 'No deposit required for online bookings.',
  } = content;

  return (
    <section className="w-full bg-white" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="mx-auto max-w-xl rounded-3xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-8 text-center shadow-sm">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">
            <Sparkles className="h-3.5 w-3.5" />
            {eyebrow}
          </span>
          <h2 className="mt-4 text-2xl font-bold text-slate-900">{headline}</h2>
          <div className="mt-5 flex items-baseline justify-center gap-1">
            <span className="text-5xl font-bold text-slate-900">{price}</span>
            <span className="text-sm text-slate-500">
              {currency} {period}
            </span>
          </div>
          <p className="mx-auto mt-3 max-w-sm text-sm text-slate-600">{description}</p>

          <ul className="mx-auto mt-6 grid max-w-sm gap-2 text-left">
            {features.map((f: string, i: number) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                {f}
              </li>
            ))}
          </ul>

          <a href={safeHref(ctaHref)} className="mt-7 inline-flex w-full items-center justify-center rounded-xl bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand/90">
            {ctaText}
          </a>
          {note && <p className="mt-3 text-xs text-slate-500">{note}</p>}
        </div>
      </div>
    </section>
  );
}

export const PRICING_VARIATIONS = {
  cards: PricingCards,
  table: PricingTable,
  single: PricingSingle,
} as const;

export type PricingVariationKey = keyof typeof PRICING_VARIATIONS;
